import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";

const tokenizeLabels = (text) =>
  String(text || "")
    .split(/,+/g)
    .map((label) => label.trim())
    .filter(Boolean);

const getLabelKey = (label) => String(label || "").trim().toLowerCase();

const dedupeLabels = (labels = []) => {
  const seen = new Set();

  return labels.reduce((acc, label) => {
    const trimmedLabel = String(label || "").trim();
    const labelKey = getLabelKey(trimmedLabel);

    if (!labelKey || seen.has(labelKey)) {
      return acc;
    }

    seen.add(labelKey);
    acc.push(trimmedLabel);
    return acc;
  }, []);
};

export const isLabelsPromptField = (fieldKey = "", fieldSchema = {}) => {
  const values = [fieldKey, fieldSchema?.title]
    .map((value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, ""))
    .filter(Boolean);

  return values.some((value) =>
    value === "labelspromptkey" ||
    value === "labelpromptkey" ||
    value === "labelsprompt"
  );
};

export default function LabelChipsInput({
  value = "",
  onChange,
  invalid = false,
  disabled = false,
  readonly = false,
  placeholder,
  ariaLabel = "Labels",
}) {
  const [labelDraft, setLabelDraft] = useState("");
  const [duplicateMessage, setDuplicateMessage] = useState("");
  const labelInputRef = useRef(null);
  const canEdit = !disabled && !readonly;

  const labelNames = useMemo(
    () => dedupeLabels(String(value || "").split(",")),
    [value]
  );
  const normalizedValue = useMemo(() => labelNames.join(","), [labelNames]);

  useEffect(() => {
    setLabelDraft("");
  }, [normalizedValue]);

  useEffect(() => {
    if (!value || String(value) === normalizedValue) return;
    onChange?.(normalizedValue);
  }, [normalizedValue, onChange, value]);

  const syncLabels = useCallback((nextLabels) => {
    if (!canEdit) return;

    const normalizedLabels = (nextLabels || [])
      .map((label) => String(label).trim())
      .filter(Boolean);
    const cleaned = dedupeLabels(normalizedLabels);

    setDuplicateMessage(
      normalizedLabels.length !== cleaned.length
        ? "Duplicate labels are not allowed."
        : ""
    );

    onChange?.(cleaned.join(","));
  }, [canEdit, onChange]);

  const commitLabelDraft = useCallback(() => {
    if (!canEdit) return labelNames;

    const tokens = tokenizeLabels(labelDraft);
    if (!tokens.length) return labelNames;

    const nextLabels = [...labelNames, ...tokens];
    setLabelDraft("");
    syncLabels(nextLabels);
    return nextLabels;
  }, [canEdit, labelDraft, labelNames, syncLabels]);

  const removeLabelAt = useCallback((idx) => {
    syncLabels(labelNames.filter((_, labelIdx) => labelIdx !== idx));
  }, [labelNames, syncLabels]);

  return (
    <div
      className={`dataset-label-chip-input ${invalid ? "dataset-label-chip-input--invalid" : ""} ${!canEdit ? "dataset-label-chip-input--disabled" : ""}`}
      onClick={() => canEdit && labelInputRef.current?.focus()}
      role="presentation"
    >
      {labelNames.map((labelName, idx) => (
        <span
          key={`${labelName}-${idx}`}
          className="dataset-label-chip"
        >
          <span>{labelName}</span>
          {canEdit && (
            <button
              type="button"
              className="dataset-label-chip__remove"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                removeLabelAt(idx);
              }}
              aria-label={`Remove ${labelName}`}
            >
              <FontAwesomeIcon icon={faTimesCircle} />
            </button>
          )}
        </span>
      ))}
      <input
        ref={labelInputRef}
        className="dataset-label-chip-input__control"
        value={labelDraft}
        placeholder={
          placeholder ||
          (labelNames.length
            ? "Add another label..."
            : "Enter label, press comma ...")
        }
        aria-label={ariaLabel}
        disabled={!canEdit}
        onChange={(e) => {
          const nextValue = e.target.value;
          setDuplicateMessage("");

          if (!nextValue.includes(",")) {
            setLabelDraft(nextValue);
            return;
          }

          const tokens = tokenizeLabels(nextValue);
          const endsWithDelimiter = nextValue.endsWith(",");
          const hasMultiplePastedTokens = !endsWithDelimiter && tokens.length > 1;
          const completeTokens =
            endsWithDelimiter || hasMultiplePastedTokens ? tokens : tokens.slice(0, -1);
          const remainder =
            endsWithDelimiter || hasMultiplePastedTokens ? "" : (tokens[tokens.length - 1] || "");

          if (completeTokens.length) {
            syncLabels([...labelNames, ...completeTokens]);
          }
          setLabelDraft(remainder);
        }}
        onKeyDown={(e) => {
          if (e.key === ",") {
            e.preventDefault();
            commitLabelDraft();
            return;
          }

          if (e.key === "Backspace" && !labelDraft && labelNames.length > 0) {
            e.preventDefault();
            removeLabelAt(labelNames.length - 1);
            return;
          }

          if (e.key === "Enter") {
            e.preventDefault();
            commitLabelDraft();
          }
        }}
        onBlur={commitLabelDraft}
      />
      {duplicateMessage && (
        <div className="text-danger small w-100" role="alert" aria-live="polite">
          {duplicateMessage}
        </div>
      )}
    </div>
  );
}
